/**
 * Which way a map has to TURN to show a hole running up the screen.
 *
 * This lives outside the admin tree because there are now two OpenLayers
 * surfaces asking the same question — the course-geometry editor
 * (`admin/course-geometry/MapCanvas.tsx`) and the club's public round page
 * (`lib/holeMap.ts` and the component built on it) — and only one of them may
 * pay for OpenLayers. `admin/course-geometry/geo.ts`, where these two
 * functions were written, imports `ol/proj` at module top: a club page
 * importing them from there would pull a map library into the bundle every
 * member loads to read a scorecard, in order to answer a question that is pure
 * arithmetic over an angle. So the arithmetic moved here, `geo.ts` re-exports
 * both, and every admin caller and the suite that pins them are unchanged.
 * Nothing in this file imports from `ol`, and `__tests__/mapRotation.test.ts`
 * reads the source to keep it that way.
 */

const TWO_PI = Math.PI * 2

/**
 * The map rotation that puts `bearing` at the TOP of the screen — the number
 * `View#setRotation` wants. A view rotated by R shows compass bearing −R at the
 * top, so this is −bearing, wrapped to (−π, π]. Null bearing → 0, north-up.
 *
 * The wrap is not cosmetic. It turns the SHORT way round, and it is what lets
 * OpenLayers' own snap-to-zero constraint (anything within 5° of upright) catch
 * a hole that plays a degree or two WEST of north: unwrapped that is a rotation
 * of −359°, which the constraint reads as an enormous turn and leaves alone.
 */
export function mapRotationFor(bearing: number | null): number {
  if (bearing === null) return 0

  const wrapped = ((((-bearing + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI) - Math.PI
  // Due south lands on the open end of the interval; π and −π are the same
  // orientation, and the positive one is the tidier thing to report.
  return wrapped === -Math.PI ? Math.PI : wrapped
}

/**
 * A map rotation as WHOLE DEGREES in [0, 360) — the value behind the
 * `data-geometry-rotation` marker, so an agent can assert the map's orientation
 * without measuring pixels. 0 is north-up, which is also what an unrotated map
 * and a hole playing within half a degree of north both report; on a compass
 * those are the same answer.
 */
export function rotationDegrees(rotation: number): number {
  const degrees = Math.round((rotation * 180) / Math.PI)
  return ((degrees % 360) + 360) % 360
}
